import { Intent } from '@blueprintjs/core';
import * as React from 'react';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

import { useAppDispatch } from 'src/ui/app/hooks';
import { queueMessage } from 'src/ui/features/notifications';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import {
  Connection,
  useSyncPlatformSduUsageMutation,
} from 'src/ui/graph/types';
import * as copy from 'src/ui/templates/copy';
import {
  GraphError,
  getErrorFromGraphQL,
} from 'src/ui/utils/getErrorFromGraphQL';

function getErrorMessage(error: unknown): string {
  if (error && typeof error === 'object' && 'message' in error) {
    return getErrorFromGraphQL(error as GraphError).message;
  }
  if (error instanceof Error) return error.message;
  return 'Unknown error occurred';
}

export const useSyncSdu = (connection: Connection) => {
  const { org = '' } = useParams<{ org?: string }>();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  const { mutateAsync: syncPlatformSduUsage } = useSyncPlatformSduUsageMutation(
    graphQLClient
  );

  return React.useCallback(async () => {
    try {
      dispatch(
        queueMessage({
          message: copy.components.stardogUnitUsage.syncAttempting(
            connection.name
          ),
          intent: Intent.NONE,
        })
      );

      const result = await syncPlatformSduUsage({
        connectionId: connection.id,
        org_domain: org,
      });

      const data = result?.syncPlatformSduUsage;
      if (data?.status === 'success') {
        queryClient.invalidateQueries(['getPlatformTrackingStatus']);
        queryClient.invalidateQueries(['getTotalUnitUsage']);
        queryClient.invalidateQueries(['getUnitUsage']);
        queryClient.invalidateQueries(['getEndpointLastSynced']);
        let successMessage: string;
        let intent: Intent;
        if (data.recordsStored === 0) {
          successMessage = `${copy.components.stardogUnitUsage.syncSuccessNoData} "${connection.name}".`;
          intent = Intent.WARNING;
        } else {
          successMessage = `${copy.components.stardogUnitUsage.syncSuccess} "${connection.name}".`;
          intent = Intent.SUCCESS;
        }
        dispatch(
          queueMessage({
            message: successMessage,
            intent,
          })
        );
      } else {
        // Use server error as-is (server includes connection name).
        const errorMsg =
          data?.error ??
          `${copy.components.stardogUnitUsage.downloadSyncFailed} "${connection.name}".`;
        dispatch(
          queueMessage({
            message: errorMsg,
            intent: Intent.DANGER,
          })
        );
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      dispatch(
        queueMessage({
          message: `${copy.components.stardogUnitUsage.downloadSyncFailed} "${connection.name}": ${errorMessage}`,
          intent: Intent.DANGER,
        })
      );
    }
  }, [connection, dispatch, org, queryClient, syncPlatformSduUsage]);
};
